import { PaymentMethod, Plan } from '@lucidtech/las-sdk-browser';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useSessionStorage } from '@uidotdev/usehooks';
import { HTMLProps, memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Flex, Heading, Separator, Switch, Text } from '@/components/core';
import { CheckmarkIcon } from '@/components/icons';
import { PENDING_PLAN_ID_KEY } from '@/constants';
import { useRemainingDaysOfTrial } from '@/hooks';
import { toPaymentMethodId, useCreatePaymentMethod, useGetPaymentMethod, useListPlans } from '@/hooks/api';
import { useOrganizationStore } from '@/store';

import { Page } from './Page';
import { createAndGroupPlans, enterprisePlan, planFeatures, toCanonicalPlanId } from './planUtils.ts';
import { UpgradePlanModal, UpgradePlanModalProps } from './UpgradePlanModal.tsx';
import { WorkspaceHeader } from './WorkspaceHeader.tsx';

const SHOW_ANNUAL_BILLING_KEY = 'cradl:showAnnualBilling';

type PlanDetailsProps = {
  plan: Plan;
  isCurrent: boolean;
  isCustom: boolean;
} & HTMLProps<HTMLDivElement>;

const PlanDetails = ({ plan, isCurrent, isCustom, ...rest }: PlanDetailsProps) => {
  const flatAmount = Number(plan.license?.flatAmount);
  const billingCycle = Number(plan.license?.billingCycle);
  const monthlyPrice = flatAmount / billingCycle;

  const ctaText = { Individual: 'Choose plan', Team: 'Choose plan', Enterprise: 'Contact us' };
  const currentPlan = plan.name as keyof typeof planFeatures;
  const featureHeader = { Individual: 'Includes', Team: 'Everything in Individual', Enterprise: 'Everything in Team' };
  const features = plan.name ? (planFeatures[plan.name as keyof typeof planFeatures] ?? []) : [];

  const [getPaymentMethodPromise, setGetPaymentMethodPromise] = useState<Promise<PaymentMethod> | null>(null);

  const paymentMethodId = useOrganizationStore((state) => state.paymentMethodId);
  const { data: paymentMethod } = useGetPaymentMethod(toPaymentMethodId(paymentMethodId));
  const { mutateAsync: createPaymentMethod } = useCreatePaymentMethod();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setPendingPlanId] = useSessionStorage<string | undefined>(PENDING_PLAN_ID_KEY, undefined);

  const getPaymentMethod = useCallback(async () => {
    if (paymentMethod) return paymentMethod;
    return createPaymentMethod({});
  }, [createPaymentMethod, paymentMethod]);

  const choosePlan = useCallback(() => {
    setGetPaymentMethodPromise(getPaymentMethod());
    setPendingPlanId(plan.planId);
  }, [getPaymentMethod, plan.planId, setPendingPlanId]);

  const upgradePlanProps = useMemo(() => {
    const selectedPlanId = `${plan.organizationId}/${plan.planId}`;
    const props = { defaultPlanId: selectedPlanId } as Omit<UpgradePlanModalProps, 'getPaymentMethodPromise'>;
    if (billingCycle === 1) {
      props.monthlyPlanId = selectedPlanId;
      props.yearlyPlanId = selectedPlanId.replace('monthly', 'yearly');
    } else {
      props.monthlyPlanId = selectedPlanId.replace('yearly', 'monthly');
      props.yearlyPlanId = selectedPlanId;
    }
    return props;
  }, [billingCycle, plan.organizationId, plan.planId]);

  const navigate = useNavigate();

  const closeUpgradeModal = useCallback(async () => {
    setGetPaymentMethodPromise(null);
    return navigate({ to: '.', search: {} });
  }, [navigate]);

  const { upgradePlanId } = useSearch({ from: '/_auth/_org/settings/plans' });

  const showUpgradeModal = useMemo(() => {
    return upgradePlanProps && (upgradePlanId === plan.planId || getPaymentMethodPromise !== null);
  }, [getPaymentMethodPromise, plan.planId, upgradePlanId, upgradePlanProps]);

  return (
    <div
      className="aria-selected:border-primary-200 flex grow flex-col gap-2 rounded-xl border-2 border-gray-100 bg-white p-6 shadow-md"
      aria-selected={isCurrent}
      {...rest}
    >
      <div className="flex items-center">
        <div className="flex grow flex-col gap-1">
          <Heading>{plan?.name}</Heading>

          {isCustom ? (
            <Text>Custom</Text>
          ) : (
            <>
              <div className="flex items-end gap-2">
                <Text>
                  {plan.currency === 'USD' ? '$' : plan.currency}
                  {monthlyPrice} / month
                </Text>
              </div>
              <Text>{plan.description}</Text>
            </>
          )}
        </div>

        {isCurrent && (
          <div className="bg-primary-100 text-primary-700 border-primary-200 rounded-sm border-1 px-1 py-0 text-xs font-medium">
            Current plan
          </div>
        )}
      </div>

      {isCustom ? (
        <Button
          variant={isCurrent ? 'secondary' : 'primary'}
          isDisabled={isCurrent}
          onPress={() => window.open('https://cal.com/cradlai/30min', '_blank')}
        >
          <Text>{isCurrent ? `You are on ${plan.name}` : (ctaText[currentPlan] ?? 'Choose plan')} </Text>
        </Button>
      ) : (
        <>
          <Button
            variant={isCurrent || getPaymentMethodPromise !== null ? 'secondary' : 'primary'}
            isDisabled={isCurrent || getPaymentMethodPromise !== null}
            onPress={choosePlan}
          >
            <Text>{isCurrent ? `You are on ${plan.name}` : (ctaText[currentPlan] ?? 'Choose plan')} </Text>
          </Button>
          <UpgradePlanModal
            close={closeUpgradeModal}
            isOpen={showUpgradeModal}
            getPaymentMethodPromise={getPaymentMethodPromise}
            {...upgradePlanProps}
          />
        </>
      )}
      <Separator orientation="horizontal" />

      <div className="flex flex-col gap-2">
        <Text>{featureHeader[currentPlan] ?? 'Includes'}</Text>
        <ul>
          {features.map((feature: string, idx: number) => (
            <>
              <li key={idx} className="mt-2 flex items-center gap-2">
                <CheckmarkIcon variant="success" />
                <Text>{feature}</Text>
              </li>
              {idx === 2 ? <Separator orientation="horizontal" className="my-3" /> : null}
            </>
          ))}
        </ul>
      </div>
    </div>
  );
};

export type PlansProps = object;

// eslint-disable-next-line no-empty-pattern
const IPlans = ({}: PlansProps) => {
  const planId = useOrganizationStore((state) => state.planId);
  const [annualBilling, setAnnualBilling] = useSessionStorage<boolean | undefined>(SHOW_ANNUAL_BILLING_KEY, undefined);

  const { data: plans } = useListPlans();
  const groupedPlans = createAndGroupPlans(plans?.data);

  const currentPlan = [...groupedPlans.monthly, ...groupedPlans.annually].find(
    (p) => toCanonicalPlanId(p.planId) === toCanonicalPlanId(planId)
  );

  useEffect(() => {
    if (annualBilling == null) {
      setAnnualBilling(currentPlan?.license?.billingCycle === 12);
    }
  }, [annualBilling, currentPlan, setAnnualBilling]);

  const { hasUpgraded } = useRemainingDaysOfTrial();

  return (
    <Page className="w-[55rem]">
      <WorkspaceHeader>
        <Heading>Plans</Heading>
        <Text>View your plan information or switch plans according to your needs.</Text>
      </WorkspaceHeader>

      <Flex className="gap-md justify-end">
        <Text className="aria-[selected=true]:font-bold" aria-selected={!annualBilling}>
          Billed monthly
        </Text>
        <Switch isSelected={annualBilling} onChange={setAnnualBilling} />
        <Text className="aria-[selected=true]:font-bold" aria-selected={annualBilling}>
          Billed yearly
        </Text>
      </Flex>

      <div className="gap-md grid w-full grid-cols-3">
        {groupedPlans[annualBilling ? 'annually' : 'monthly'].map((plan) => (
          <PlanDetails
            key={plan.planId}
            plan={plan}
            isCurrent={hasUpgraded && plan.planId === currentPlan?.planId}
            isCustom={plan.planId === enterprisePlan.planId}
          />
        ))}
      </div>
    </Page>
  );
};

const IPlansWrapper = (props: PlansProps) => {
  return <IPlans {...props} />;
};

export const Plans = memo(IPlansWrapper) as typeof IPlansWrapper;
